import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppStateProvider, useAppState } from './context/AppStateProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import NavSidebar from './components/navigation/NavSidebar';
import UtilityPanel from './components/utility/UtilityPanel';
import WorkspaceCommandCenter from './components/workspace/WorkspaceCommandCenter';
import WorkspaceTools from './components/workspace/WorkspaceTools';
import WorkspaceCapture from './components/workspace/WorkspaceCapture';
import WorkspacePipeline from './components/workspace/WorkspacePipeline';
import WorkspaceIdeas from './components/workspace/WorkspaceIdeas';
import WorkspacePortfolio from './components/workspace/WorkspacePortfolio';
import WorkspacePrompts from './components/workspace/WorkspacePrompts';
import WorkspaceBookmarks from './components/workspace/WorkspaceBookmarks';
import DraftRecoveryDialog from './components/dialogs/DraftRecoveryDialog';
import { setupAxiosInterceptor } from './utils/axiosSetup';
import { getDraft, clearDraft, getLastRoute } from './utils/draftRecovery';

const WorkspaceRouter = () => {
  const { currentView } = useAppState();

  switch (currentView) {
    case 'command-center':
      return <WorkspaceCommandCenter />;
    case 'capture':
      return <WorkspaceCapture />;
    case 'pipeline':
      return <WorkspacePipeline />;
    case 'ideas':
      return <WorkspaceIdeas />;
    case 'portfolio':
      return <WorkspacePortfolio />;
    case 'tools':
      return <WorkspaceTools />;
    case 'prompts':
      return <WorkspacePrompts />;
    case 'bookmarks':
      return <WorkspaceBookmarks />;
    default:
      return <WorkspaceCommandCenter />;
  }
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { currentView, setCurrentView } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(null);

  // Setup axios interceptor
  useEffect(() => {
    if (user) {
      setupAxiosInterceptor(
        () => user?.id,
        () => location.pathname,
        () => {
          // On session expiration, logout and redirect to login
          logout();
          navigate('/login');
        }
      );
    }
  }, [user, location, logout, navigate]);

  // Check for draft on mount
  useEffect(() => {
    if (user) {
      const draft = getDraft(user.id);
      if (draft) {
        setRecoveredDraft(draft);
        setShowDraftDialog(true);
        // Navigate to last route
        if (draft.route && draft.route !== '/') {
          const viewName = draft.route.substring(1) || 'command-center';
          setCurrentView(viewName);
        }
      }
    }
  }, [user]);

  const handleRestoreDraft = () => {
    // The draft data will be available in recoveredDraft.formData
    // This can be passed to child components via context or props
    // For now, we'll store it in sessionStorage for components to pick up
    if (recoveredDraft) {
      sessionStorage.setItem('restored_draft', JSON.stringify(recoveredDraft.formData));
    }
    setShowDraftDialog(false);
    clearDraft();
  };

  const handleDiscardDraft = () => {
    setShowDraftDialog(false);
    setRecoveredDraft(null);
    clearDraft();
  };

  return (
    <div className="dashboard" data-testid="dashboard">
      {showDraftDialog && (
        <DraftRecoveryDialog
          draft={recoveredDraft}
          onRestore={handleRestoreDraft}
          onDiscard={handleDiscardDraft}
        />
      )}
      <NavSidebar />
      <main className="workspace" data-testid="workspace">
        <WorkspaceRouter />
      </main>
      <UtilityPanel />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppStateProvider>
                  <Dashboard />
                </AppStateProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
